import React, { createContext, useContext, useEffect, useState } from "react";
import Get from "./Get";

const BinanceTickerContext = createContext({});
const TICKER_POLL_MS = 3000;
const FIAT_RATES_URL = "https://open.er-api.com/v6/latest/USD";

export function BinanceTickerProvider({ children }) {
  const [tickers, setTickers] = useState({});

  useEffect(() => {
    let reconnectTimer;
    let assetLoadTimer;
    let fiatRateTimer;
    let isUnmounted = false;
    let assetRetryDelay = 5000;
    let fiatRetryDelay = 60000;

    const pollTickers = async (pairs) => {
      if (isUnmounted) return;
      try {
        const rows = await Get.BinanceTickers();
        if (isUnmounted) return;
        setTickers((current) => {
          let next = current;
          rows.forEach((update) => {
            if (!pairs.has(update.s)) return;
            const symbol = update.s.slice(0, -4);
            const ticker = {
              price: Number(update.c),
              change: Number(update.P),
              high: Number(update.h),
              low: Number(update.l),
              volume: Number(update.v),
              quoteVolume: Number(update.q),
            };
            const previous = current[symbol];
            if (previous && Object.keys(ticker).every((key) => previous[key] === ticker[key])) return;
            if (next === current) next = { ...current };
            next[symbol] = ticker;
          });
          return next;
        });
      } catch {
        // keep last known prices and retry on the next poll
      }
      if (!isUnmounted) reconnectTimer = window.setTimeout(() => pollTickers(pairs), TICKER_POLL_MS);
    };

    const loadFiatRates = async (symbols) => {
      if (isUnmounted || symbols.length === 0) return;
      try {
        const response = await fetch(FIAT_RATES_URL);
        if (!response.ok) throw new Error("Could not load fiat exchange rates");
        const data = await response.json();
        if (data.result !== "success" || !data.rates) throw new Error("Fiat exchange rates are unavailable");

        const updates = {};
        symbols.forEach((symbol) => {
          const usdRate = Number(data.rates[symbol]);
          if (Number.isFinite(usdRate) && usdRate > 0) {
            updates[symbol] = { price: 1 / usdRate, change: null, high: null, low: null, volume: null, quoteVolume: null };
          }
        });
        if (Object.keys(updates).length > 0 && !isUnmounted) {
          setTickers((current) => ({ ...current, ...updates }));
        }

        fiatRetryDelay = 60000;
        const nextUpdate = Number(data.time_next_update_unix) * 1000;
        const refreshDelay = Number.isFinite(nextUpdate) ? Math.max(60000, nextUpdate - Date.now() + 5000) : 3600000;
        fiatRateTimer = window.setTimeout(() => loadFiatRates(symbols), refreshDelay);
      } catch {
        if (isUnmounted) return;
        fiatRateTimer = window.setTimeout(() => loadFiatRates(symbols), fiatRetryDelay);
        fiatRetryDelay = Math.min(fiatRetryDelay * 2, 3600000);
      }
    };

    const loadAssets = async () => {
      let assets = [];
      try {
        const response = await Get.MarketAssets();
        if (isUnmounted) return;
        if (response?.success === false) throw new Error("Could not load market assets");
        assets = response?.data?.data ?? response?.data ?? response;
        if (!Array.isArray(assets)) throw new Error("Market assets response is invalid");
        assetRetryDelay = 5000;
      } catch {
        if (isUnmounted) return;
        reconnectTimer = window.setTimeout(loadAssets, assetRetryDelay);
        assetRetryDelay = Math.min(assetRetryDelay * 2, 60000);
        return;
      }

      if (isUnmounted) return;
      const marketPairs = [...new Set(assets
        .map((asset) => String(asset.market_symbol ?? asset.symbol ?? "").toUpperCase().replace(/[^A-Z0-9]/g, ""))
        .filter((symbol) => /^[A-Z0-9]+USDT$/.test(symbol)))];
      const fiatSymbols = [...new Set(assets
        .filter((asset) => (asset.networks ?? asset.base_asset?.networks ?? []).some((network) => network.symbol === "FIAT_MONEY" || network.name === "FIAT"))
        .map((asset) => String(asset.asset ?? asset.base_asset?.symbol ?? "").toUpperCase())
        .filter((symbol) => /^[A-Z]{3}$/.test(symbol)))];

      loadFiatRates(fiatSymbols);

      if (isUnmounted || marketPairs.length === 0) return;
      pollTickers(new Set(marketPairs));
    };

    assetLoadTimer = window.setTimeout(loadAssets, 0);
    return () => {
      isUnmounted = true;
      window.clearTimeout(assetLoadTimer);
      window.clearTimeout(reconnectTimer);
      window.clearTimeout(fiatRateTimer);
    };
  }, []);

  return (
    <BinanceTickerContext.Provider value={tickers}>
      {children}
    </BinanceTickerContext.Provider>
  );
}

export function useBinanceTickers() {
  return useContext(BinanceTickerContext);
}

export function useBinanceTicker(symbol) {
  const tickers = useContext(BinanceTickerContext);
  return tickers[String(symbol ?? "").toUpperCase()];
}

export function formatTickerPrice(ticker, fallback) {
  if (!ticker) return fallback;
  const price = ticker.price;
  const decimals = price < 1 ? 8 : 2;
  return `$${price.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: decimals })}`;
}

export function formatTickerChange(ticker, fallback) {
  if (ticker?.change == null) return fallback;
  const change = ticker.change;
  return `${change > 0 ? "+" : ""}${change.toFixed(2)}%`;
}